import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API_BASE_URL from "../config";

const initialState = {
  name: '',
  email: '',
  gym: '',
  message: '',
};

const BookDemo = () => {
  const [form, setForm] = useState(initialState);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const MAX_NAME = 80;
  const MAX_EMAIL = 120;
  const MAX_GYM = 120;
  const MAX_MESSAGE = 1000;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const handleChange = (e) => {
  const { name, value } = e.target;
  let v = value;

  switch (name) {
    case 'name':
      v = value.slice(0, MAX_NAME);
      break;
    case 'email':
      v = value.slice(0, MAX_EMAIL);
      break;
    case 'gym':
      v = value.slice(0, MAX_GYM);
      break;
    case 'message':
      v = value.slice(0, MAX_MESSAGE);
      break;
    default:
      break;
  }

  setForm((prev) => ({ ...prev, [name]: v }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!emailRegex.test(form.email.trim())) {
      alert('Please enter a valid email address.');
      return;
    }

    if (!form.name.trim()) {
      alert('Please enter your name.');
      return;
    }

    setSubmitting(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/DemoBooking`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          gym: form.gym.trim(),
          message: form.message.trim(),
        }),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || 'Failed to send message');
      }

      setSubmitted(true);
      setForm(initialState);
    } catch (error) {
      alert(error.message || 'Something went wrong. Please try again later.');
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };


  return (
    <div className="min-h-screen flex flex-col bg-[#101010]">
      {/* Header */}
      <header className="w-full flex justify-center pt-6 px-4 sm:px-6 mb-6">
        <nav className="w-full max-w-6xl flex items-center justify-between">
          {/* Logo */}
          <a href="/" className="flex items-center gap-2 select-none">
            <span className="bg-lime-400 rounded-xl p-2">
              {/* Mini Icon */}
              <svg width="28" height="28" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
                <path d="M0 80C0 53.5 21.5 32 48 32l96 0c26.5 0 48 21.5 48 48l0 96c0 26.5-21.5 48-48 48l-96 0c-26.5 0-48-21.5-48-48L0 80zM64 96l0 64 64 0 0-64L64 96zM0 336c0-26.5 21.5-48 48-48l96 0c26.5 0 48 21.5 48 48l0 96c0 26.5-21.5 48-48 48l-96 0c-26.5 0-48-21.5-48-48l0-96zm64 16l0 64 64 0 0-64-64 0zM304 32l96 0c26.5 0 48 21.5 48 48l0 96c0 26.5-21.5 48-48 48l-96 0c-26.5 0-48-21.5-48-48l0-96c0-26.5 21.5-48 48-48zm80 64l-64 0 0 64 64 0 0-64zM256 304c0-8.8 7.2-16 16-16l64 0c8.8 0 16 7.2 16 16s7.2 16 16 16l32 0c8.8 0 16-7.2 16-16s7.2-16 16-16s16 7.2 16 16l0 96c0 8.8-7.2 16-16 16l-64 0c-8.8 0-16-7.2-16-16s-7.2-16-16-16s-16 7.2-16 16l0 64c0 8.8-7.2 16-16 16l-32 0c-8.8 0-16-7.2-16-16l0-160zM368 480a16 16 0 1 1 0-32 16 16 0 1 1 0 32zm64 0a16 16 0 1 1 0-32 16 16 0 1 1 0 32z"/>
              </svg>
            </span>
            <span className="text-white text-xl font-bold tracking-tight">FitScan</span>
          </a>
          <button
            type="button"
            onClick={() => navigate('/')}
            className="bg-[#181f10] hover:bg-neutral-800 text-lime-400 font-semibold border-2 border-[#2b2b2b] rounded-full px-5 py-2 text-sm btn-hover"
          >
            Back
          </button>
        </nav>
      </header>

      <main className="flex-grow flex items-center justify-center px-4">
        <div className="w-full max-w-lg bg-[#181818] p-8 sm:p-10 rounded-2xl shadow border border-[#252525]">
          <h1 className="text-2xl sm:text-3xl font-bold text-white text-center mb-2">
            Book A Demo
          </h1>
          <p className="text-gray-400 text-center mb-8">
            Want to see FitScan in action? Fill in your details and our team will schedule a live demo to show you how it works for your gym.
          </p>
          {submitted ? (
            <div className="text-center">
              <span className="text-lime-400 text-4xl block mb-4">✔️</span>
              <p className="text-white font-semibold mb-2">Thank you!</p>
              <p className="text-gray-300 mb-6">Our team will be in touch soon.</p>
              <button
                onClick={() => setSubmitted(false)}
                className="bg-lime-400 hover:bg-lime-300 text-[#181f10] font-semibold rounded-full px-6 py-2 shadow-lg btn-hover"
              >
                Book Another
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label htmlFor="name" className="block text-gray-200 mb-1 font-medium">
                  Name
                </label>
                <input
                  required
                  id="name"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  className="w-full bg-[#222] border border-[#252525] rounded-xl py-2 px-4 text-white focus:outline-none focus:border-lime-400 transition"
                  type="text"
                  placeholder="Your Name"
                  autoComplete="name"
                  maxLength={MAX_NAME}
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-gray-200 mb-1 font-medium">
                  Email
                </label>
                <input
                  required
                  id="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  className="w-full bg-[#222] border border-[#252525] rounded-xl py-2 px-4 text-white focus:outline-none focus:border-lime-400 transition"
                  type="email"
                  placeholder="you@email.com"
                  autoComplete="email"
                  maxLength={MAX_EMAIL}
                />
              </div>
              <div>
                <label htmlFor="gym" className="block text-gray-200 mb-1 font-medium">
                  Gym Name / Organization <span className="text-xs text-gray-400">(optional)</span>
                </label>
                <input
                  id="gym"
                  name="gym"
                  value={form.gym}
                  onChange={handleChange}
                  className="w-full bg-[#222] border border-[#252525] rounded-xl py-2 px-4 text-white focus:outline-none focus:border-lime-400 transition"
                  type="text"
                  placeholder="Gym or Company Name"
                  maxLength={MAX_GYM}
                />
              </div>
              <div>
                <label htmlFor="message" className="block text-gray-200 mb-1 font-medium">
                  Message <span className="text-xs text-gray-400">(optional)</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  className="w-full bg-[#222] border border-[#252525] rounded-xl py-2 px-4 text-white focus:outline-none focus:border-lime-400 transition"
                  rows={3}
                  placeholder="Message or questions"
                  maxLength={MAX_MESSAGE}
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="w-full flex justify-center items-center bg-lime-400 hover:bg-lime-300 text-[#181f10] font-semibold rounded-full px-6 py-3 shadow-lg mt-2 btn-hover disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <svg className="animate-spin mr-2 h-5 w-5 text-[#181f10]" viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v8H4z"
                      ></path>
                    </svg>
                    Booking...
                  </>
                ) : (
                  <>
                    <span>Book Demo</span>
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </main>
    </div>
  );
};

export default BookDemo;
